import { Dialog, Transition } from '@headlessui/react';
import { animated, config, useSpring, useTrail } from '@react-spring/web';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { hideTaskModal } from 'features/ui';
import { Fragment, useEffect, useState } from 'react';
import ProgressIndicator from './progress-indicator';

type AnimatedTaskModalProps = {
  animationDelay?: number;
  taskCompletionDelay?: number;
};

export default function AnimatedTaskModal({
  animationDelay = 500,
  taskCompletionDelay = 350
}: AnimatedTaskModalProps) {
  const dispatch = useAppDispatch();
  const { isOpen, tasks, title } = useAppSelector(
    (state) => state.ui.taskModal
  );
  const [completedTasks, setCompletedTasks] = useState<Set<string>>(new Set());

  const onClose = () => {
    dispatch(hideTaskModal());
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (!isOpen) {
      setCompletedTasks(new Set());
    } else if (tasks && Array.isArray(tasks) && tasks.length > 0) {
      const tasksCopy = [...tasks];
      let currentIndex = 0;

      const completeNextTask = () => {
        if (currentIndex < tasksCopy.length) {
          const currentTask = tasksCopy[currentIndex];

          if (
            currentTask &&
            typeof currentTask === 'object' &&
            currentTask.key
          ) {
            setCompletedTasks((prev) => new Set([...prev, currentTask.key]));
          }

          currentIndex += 1;

          if (currentIndex < tasksCopy.length) {
            timeout = setTimeout(completeNextTask, taskCompletionDelay);
          } else {
            timeout = setTimeout(onClose, 800);
          }
        }
      };

      timeout = setTimeout(completeNextTask, animationDelay);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isOpen, tasks, animationDelay, taskCompletionDelay]);

  const trail = useTrail(tasks?.length || 0, {
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? 'translateY(0px)' : 'translateY(20px)',
    config: config.gentle,
    delay: 200
  });

  const modalSpring = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? 'scale(1)' : 'scale(0.95)',
    config: config.gentle
  });

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as='div' className='relative z-50' onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-50 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4'>
            <animated.div style={modalSpring}>
              <Dialog.Panel className='relative transform rounded-lg bg-white px-8 py-6 shadow-xl transition-all sm:w-full sm:max-w-lg'>
                <div className='text-center'>
                  <Dialog.Title className='mb-6 text-lg font-semibold text-gray-900'>
                    {title}
                  </Dialog.Title>

                  <div className='space-y-4'>
                    {tasks?.map((task, index) => {
                      if (!task || typeof task !== 'object' || !task.key) {
                        return null;
                      }

                      const isCompleted = completedTasks.has(task.key);

                      return (
                        <animated.div
                          key={task.key}
                          style={trail[index]}
                          className='flex items-center gap-4 rounded-lg bg-gray-50 p-3'
                        >
                          <ProgressIndicator checked={isCompleted} size='md' />
                          <div className='flex-1 text-left'>
                            <div className='font-medium text-gray-900'>
                              {task.key}
                            </div>
                            <div className='text-sm text-gray-600'>
                              {isCompleted ? (
                                task.value
                              ) : (
                                <div className='flex items-center gap-2'>
                                  <div className='flex space-x-1'>
                                    <div className='h-1 w-1 animate-pulse rounded-full bg-gray-400' />
                                    <div
                                      className='h-1 w-1 animate-pulse rounded-full bg-gray-400'
                                      style={{ animationDelay: '0.2s' }}
                                    />
                                    <div
                                      className='h-1 w-1 animate-pulse rounded-full bg-gray-400'
                                      style={{ animationDelay: '0.4s' }}
                                    />
                                  </div>
                                  <span className='text-xs text-gray-500'>
                                    Loading...
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </animated.div>
                      );
                    })}
                  </div>
                </div>
              </Dialog.Panel>
            </animated.div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
